/**
 * The provider, as a mark.
 *
 * The sync chip never spells the provider's name: a 28px chip that says
 * "Procore" spends half its width on a word the mark says faster, and the
 * name is what pushed the number and the age off the end of the chip. The
 * name stays in the chip's `title` and its accessible name, so nothing is
 * lost to a screen reader or a hover.
 *
 * Procore ships its app icon in this repo (`assets/daily-log-format/
 * procore.png`, the same file the daily-log format picker draws). Autodesk
 * does not, so ACC is the Autodesk glyph on a paper tile — the shape is
 * theirs, drawn rather than imported.
 */
import procoreMark from "../../assets/daily-log-format/procore.png";

export type MarkProvider = "procore" | "autodesk";

/** Autodesk's own mark, as a single path on a 24 box. */
const AUTODESK_PATH =
  "m.129 20.202 14.7-9.136h7.625c.235 0 .445.188.445.445 0 .21-.092.305-.21.375l-7.222 4.323c-.47.283-.633.845-.633 1.265l-.008 2.725H24V4.362a.561.561 0 0 0-.585-.562h-8.752L0 12.893V20.2h.129z";

/**
 * The Procore PNG's glyph fills only the middle ~48% of its canvas (the rest
 * is transparent), so drawn bare it reads about half the size of the Autodesk
 * tile beside it. Scaling the image by this much inside a tile puts its glyph
 * at the same ~67% of the tile the Autodesk glyph takes.
 */
const PROCORE_GLYPH_SCALE = 0.67 / 0.48;

/** The paper tile the Autodesk glyph sits on — and, when `tiled`, Procore's. */
function tileStyle(size: number): React.CSSProperties {
  return {
    width: size,
    height: size,
    borderRadius: Math.round(size * 0.28),
    flex: "none",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background: "var(--card)",
    border: "1px solid rgba(10,10,10,.10)",
  };
}

export function ProviderMark({
  provider,
  size = 18,
  tiled = false,
}: {
  provider: MarkProvider;
  size?: number;
  /**
   * Draw Procore on the same tile as Autodesk, so the two marks weigh the same
   * when they sit in one list (the home project list). Off by default: the
   * record chips have always drawn Procore bare.
   */
  tiled?: boolean;
}) {
  if (provider === "procore" && tiled) {
    const img = Math.round(size * PROCORE_GLYPH_SCALE);
    return (
      <span aria-hidden data-provider-mark="procore" style={{ ...tileStyle(size), overflow: "hidden" }}>
        <img
          src={procoreMark}
          alt=""
          // maxWidth: Tailwind's preflight caps every <img> at 100% of its
          // box, which would shrink the scaled image back to the tile's width.
          style={{ width: img, height: img, maxWidth: "none", flex: "none", display: "block", objectFit: "contain" }}
        />
      </span>
    );
  }
  if (provider === "procore") {
    return (
      <img
        src={procoreMark}
        alt=""
        aria-hidden
        data-provider-mark="procore"
        style={{
          width: size,
          height: size,
          borderRadius: Math.round(size * 0.28),
          flex: "none",
          display: "block",
          objectFit: "contain",
        }}
      />
    );
  }
  return (
    <span aria-hidden data-provider-mark="autodesk" style={tileStyle(size)}>
      <svg width={Math.round(size * 0.67)} height={Math.round(size * 0.67)} viewBox="0 0 24 24" fill="var(--foreground)">
        <path d={AUTODESK_PATH} />
      </svg>
    </span>
  );
}

export default ProviderMark;
